import React, { useEffect, useRef, useState } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSelector } from 'react-redux';
import { Send, MessagesSquare } from 'lucide-react';
import { RootState } from '../../../configuration';
import { useConnectionBuilder } from '../../../hooks/use-connection/useConnection';
import { HubEvent, HubName } from '../../../enums/hubnames';
import { getClanMessages, ClanMessage } from '../../../api/group/clan-chat.api';

interface Props {
  clanId: string;
}

/** Chat nhóm của Clan (realtime qua SignalR hub /hubs/clanchat). */
const ClanChatPanel: React.FC<Props> = ({ clanId }) => {
  const me = useSelector((s: RootState) => s.features.auth.profile);
  const myId = me?.userId;
  const { connection } = useConnectionBuilder(HubName.ClanChat);

  const [messages, setMessages] = useState<ClanMessage[]>([]);
  const [input, setInput] = useState('');
  const [ready, setReady] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Tải lịch sử (BE trả mới→cũ → đảo lại để hiển thị cũ→mới)
  useEffect(() => {
    if (!clanId) return;
    getClanMessages(clanId, 1, 50)
      .then((list) => setMessages([...list].reverse()))
      .catch(() => setMessages([]));
  }, [clanId]);

  // Kết nối hub + vào phòng bang + lắng nghe tin mới
  useEffect(() => {
    if (!connection || !clanId) return;

    const onNew = (msg: ClanMessage) => {
      if (msg?.clanId === clanId) setMessages((prev) => [...prev, msg]);
    };
    connection.on(HubEvent.NewClanMessage, onNew);

    const start = async () => {
      try {
        if (connection.state === HubConnectionState.Disconnected) {
          await connection.start();
        }
        await connection.invoke('JoinClan', clanId);
        setReady(true);
      } catch {
        setReady(false);
      }
    };
    start();

    return () => {
      connection.off(HubEvent.NewClanMessage, onNew);
      if (connection.state === HubConnectionState.Connected) {
        connection.invoke('LeaveClan', clanId).catch(() => undefined);
      }
    };
  }, [connection, clanId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const content = input.trim();
    if (!content || !connection || connection.state !== HubConnectionState.Connected) return;
    try {
      await connection.invoke('SendMessage', { clanId, content });
      setInput('');
    } catch {
      // ignore
    }
  };

  return (
    <div className="ui-card mx-auto flex h-[380px] w-full max-w-[640px] flex-col overflow-hidden">
      <div className="flex items-center gap-2 border-b border-[#E5E7EB] px-4 py-3 dark:border-slate-700">
        <MessagesSquare size={18} className="text-sky-600" />
        <span className="text-sm font-semibold text-slate-800 dark:text-white">
          Chat bang hội
        </span>
        <span
          className={`ml-auto h-2 w-2 rounded-full ${ready ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
          title={ready ? 'Đã kết nối' : 'Đang kết nối…'}
        />
      </div>

      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-[#F4F5F8] px-4 py-4 dark:bg-slate-900/40">
        {messages.length === 0 && (
          <p className="pt-10 text-center text-sm text-slate-400">
            Chưa có tin nhắn nào. Hãy bắt đầu trò chuyện!
          </p>
        )}
        {messages.map((m) => {
          const mine = m.senderUserId === myId;
          return mine ? (
            <div key={m.id} className="flex justify-end">
              <div className="max-w-[78%] rounded-2xl rounded-br-md bg-sky-600 px-3.5 py-2 text-sm text-white shadow-sm">
                {m.content}
              </div>
            </div>
          ) : (
            <div key={m.id} className="flex items-end gap-2">
              <img
                src={m.senderAvatar || '/default-avatar.png'}
                alt=""
                className="h-7 w-7 shrink-0 rounded-full bg-slate-200 object-cover dark:bg-slate-700"
              />
              <div className="max-w-[78%]">
                <p className="mb-0.5 text-xs text-slate-500 dark:text-slate-400">{m.senderName}</p>
                <div className="rounded-2xl rounded-bl-md bg-white px-3.5 py-2 text-sm text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100">
                  {m.content}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-[#E5E7EB] bg-white p-3 dark:border-slate-700 dark:bg-dark2">
        <div className="flex items-end gap-2 rounded-2xl border border-[#C8CCD8] bg-white px-3 py-2 focus-within:border-sky-500 dark:border-slate-600 dark:bg-slate-800">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Nhắn cho bang hội…"
            className="max-h-24 flex-1 resize-none bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-slate-500"
          />
          <button
            type="button"
            aria-label="Gửi"
            onClick={send}
            disabled={!input.trim() || !ready}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white transition-all hover:bg-sky-700 active:scale-95 disabled:opacity-40"
          >
            <Send size={17} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClanChatPanel;
